import React, { useMemo } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import LinkIcon from '@mui/icons-material/Link'
import BaseDialog from '../shared/dialogs/BaseDialog'
import DialogFooter from '../shared/dialogs/DialogFooter'

import { useAuth } from '../../contexts/AuthContext'
import { extractSummary, getReleases, stripLeadingParagraph } from '../../utils/releases'
import { typePillChipProps } from '../shared/AppDrawer/chipPresets'
import { poolwerxColors } from '../../theme/colors'
import { generateFullUrl } from '../../utils/urls'
import { formatDate } from '../../utils/formatters'
import { useNotify } from '../shared/ToastProvider'
import { ReleaseBody } from './ReleaseBody'
import ReleaseIntro from './ReleaseIntro'
import PscNotesPanel from './PscNotesPanel'
import { LATEST_CHIP_LABEL } from './ReleaseCard'

interface ReleaseNotesDialogProps {
  open: boolean
  onClose: () => void
}

// The one shared release modal. Rendered identically from both entry points -
// the auto-popup (ReleaseNotesAutoLauncher) and the version-number click
// (VersionDisplay) - so the two can never drift. The "New Inspector Tool
// Release" framing (heading + `announcement` variant icon, with the "What's
// new in v…" sub-heading) is intrinsic to the modal, not a per-caller flag.
const ReleaseNotesDialog: React.FC<ReleaseNotesDialogProps> = ({ open, onClose }) => {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotify()
  const releases = useMemo(() => getReleases(user?.role), [user?.role])
  const latest = releases[0]

  const hasFpBody = Boolean(latest?.body && latest.body.trim())
  const hasPscBody = Boolean(latest?.internalBody && latest.internalBody.trim())
  const formattedDate = latest?.date ? formatDate(latest.date) : ''
  const summary = useMemo(
    () => (latest ? extractSummary(latest.body) : ''),
    [latest],
  )
  const bodyAfterIntro = useMemo(
    () => (latest ? stripLeadingParagraph(latest.body) : ''),
    [latest],
  )

  const handleCopyLink = async () => {
    if (!latest) return
    const url = generateFullUrl(`/releases#v${latest.version}`)
    try {
      await navigator.clipboard.writeText(url)
      showSuccess('Link copied')
    } catch {
      showError('Failed to copy link')
    }
  }

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title={latest ? 'New Inspector Tool Release' : "What's new"}
        subtitle={latest ? `What's new in v${latest.version}` : undefined}
        variant="announcement"
        size="md"
        // Sit above the inspector ExceptionDrawer (a custom side panel pinned at
        // zIndex.modal + 10, its menus at + 20) so an auto-popped release notice
        // is never covered by an open drawer.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 30 }}
        contentSx={{ overflowX: 'hidden' }}
        footer={
          <DialogFooter
            link={{ label: 'View full release history', href: '/releases', external: true }}
          />
        }
      >
        {!latest ? (
          <Typography variant="body2" color="text.secondary">
            No release notes are available yet.
          </Typography>
        ) : (
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Stack spacing={0.5} sx={{ mb: 1.25, minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flexShrink: 1,
                  }}
                >
                  {latest.title}
                </Typography>
                <Chip {...typePillChipProps(`v${latest.version}`)} />
                <Tooltip title="Copy link to this release">
                  <IconButton
                    size="small"
                    aria-label={`Copy link to v${latest.version}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      void handleCopyLink()
                    }}
                    sx={{ color: 'text.secondary' }}
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                <Chip
                  label={LATEST_CHIP_LABEL}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    backgroundColor: poolwerxColors.teal,
                    letterSpacing: 0.4,
                    flexShrink: 0,
                  }}
                />
              </Stack>
              {(formattedDate || summary) && (
                <Stack spacing={0.875} sx={{ mt: '-5px' }}>
                  {formattedDate && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ color: 'text.secondary' }}
                    >
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 12 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          letterSpacing: 0.2,
                          textTransform: 'uppercase',
                          fontWeight: 600,
                        }}
                      >
                        {formattedDate}
                      </Typography>
                    </Stack>
                  )}
                  <ReleaseIntro text={summary} />
                </Stack>
              )}
            </Stack>
            {hasFpBody && bodyAfterIntro && (
              <ReleaseBody body={bodyAfterIntro} decorateHeadings />
            )}
            {hasPscBody && (
              <PscNotesPanel body={latest.internalBody as string} />
            )}
          </Box>
        )}
      </BaseDialog>
    </>
  )
}

export default ReleaseNotesDialog
